/**
 * Spec 027 — `<QrScanner>`: camera-based QR-code reader.
 *
 * Wraps `html5-qrcode@^2.3.8` (Apache-2.0, on-device — no network calls; see
 * research.md §Decision 3 / Constitution II privacy gate). Camera permission
 * is requested **on user interaction only** (FR-014); the stream is fully
 * stopped on unmount and on first successful decode.
 *
 * Rendering: a square viewfinder `<div>` whose ID is wired to the html5-qrcode
 * scanner instance. Decoded payloads → `onDecode`. Permission failures →
 * `onError`.
 *
 * Defensive against React StrictMode double-mounting: html5-qrcode's
 * `scanner.stop()` throws synchronously when the scanner has not finished
 * starting yet ("Cannot stop, scanner is not running or paused"). We guard
 * via `getState() === Html5QrcodeScannerState.SCANNING` AND wrap in
 * try/catch since the lib's behaviour varies by version.
 */
import { useEffect, useId, useRef, useState } from 'react';

import {
  Html5Qrcode,
  Html5QrcodeScannerState,
  Html5QrcodeSupportedFormats,
} from 'html5-qrcode';

export interface QrScannerProps {
  /** Fired exactly once per successful decode. */
  onDecode(text: string): void;
  /** Fired on camera-permission denial or device-not-available. */
  onError?(error: Error): void;
  /** Square viewfinder side-length in CSS pixels. Default 240. */
  size?: number;
}

const safeStop = async (scanner: Html5Qrcode | null): Promise<void> => {
  if (!scanner) return;
  try {
    if (scanner.getState() === Html5QrcodeScannerState.SCANNING) {
      await scanner.stop();
    }
  } catch {
    // swallow — already stopped or never started
  }
  try {
    scanner.clear();
  } catch {
    // ignore
  }
};

export const QrScanner: React.FC<QrScannerProps> = ({
  onDecode,
  onError,
  size = 240,
}) => {
  const viewfinderId = useId().replace(/[:]/g, '_');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isStarting, setIsStarting] = useState(true);
  const decodedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const scanner = new Html5Qrcode(viewfinderId, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    });
    scannerRef.current = scanner;

    const start = async () => {
      try {
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: size - 40, height: size - 40 } },
          (text) => {
            if (decodedRef.current || cancelled) return;
            decodedRef.current = true;
            void safeStop(scanner).then(() => {
              if (!cancelled) onDecode(text);
            });
          },
          /* onDecodeError */ () => {
            // Per-frame decode misses are normal — silent.
          },
        );
        if (cancelled) {
          // Component unmounted while we awaited start — clean up immediately.
          await safeStop(scanner);
          return;
        }
        setIsStarting(false);
      } catch (err) {
        if (!cancelled && onError) {
          onError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    };
    void start();

    return () => {
      cancelled = true;
      void safeStop(scannerRef.current);
      scannerRef.current = null;
    };
    // size and callbacks intentionally captured at mount — re-mount to swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      id={viewfinderId}
      role="region"
      aria-label="QR code camera viewfinder"
      aria-busy={isStarting}
      style={{
        width: size,
        height: size,
        background: 'var(--color-neutral-95, #F5EFF7)',
        borderRadius: 'var(--radius-md, 12px)',
        overflow: 'hidden',
      }}
    />
  );
};

export default QrScanner;
